<template>
  <div class="py-2 px-2 container-fluid modern-scrollbar" v-if="!profile || inProgress">
    <div class="profile-grid">
      <div class="card area-main">
        <div class="card-body">
          <div class="d-flex justify-content-between mb-4">
            <NSkeleton text style="width: 40%; height: 1.75rem;" />
            <NSkeleton text style="width: 6rem; height: 2rem;" />
          </div>
          <NSkeleton text :repeat="4" />
          <NSkeleton text style="width: 75%" />
          <div class="mt-3">
            <NSkeleton text :repeat="3" />
            <NSkeleton text style="width: 55%" />
          </div>
        </div>
      </div>
      <div class="card area-sidebar">
        <div class="card-body">
          <div class="mb-4">
            <NSkeleton text style="width: 50%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 80%" />
            <NSkeleton text style="width: 65%" />
          </div>
          <div class="mb-4">
            <NSkeleton text style="width: 45%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 90%" />
            <NSkeleton text style="width: 70%" />
          </div>
          <div class="mb-4">
            <NSkeleton text style="width: 40%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 85%" />
            <NSkeleton text style="width: 75%" />
          </div>
        </div>
      </div>
      <div class="card area-map">
        <div class="card-body px-0 pb-1">
          <NSkeleton style="width: 100%; height: 400px;" />
          <div class="m-2 mx-3">
            <NSkeleton text style="width: 50%" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="py-2 px-2 container-fluid modern-scrollbar" v-else>
    <div class="profile-grid">

      <!-- Main Info Card -->
      <div class="card area-main">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <h3 class="mb-4 proxima-blue-text">{{ profile.companyName }}</h3>
            <div>
              <NButton type="info" @click="addNote" color="#213ce5"
                       style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent;  border-radius: 3px !important;"
                       @mouseenter="e => e.currentTarget.style.border = '1px solid #00aeef'"
                       @mouseleave="e => e.currentTarget.style.border = '1px solid transparent'">
                {{ has_notes("COMPANY", route) ? $t("CompanyProfile.viewNotesButton") : $t("CompanyProfile.addNoteButton") }}
              </NButton>
            </div>
          </div>
          <InfoText
              class="mb-3"
              v-if="profile.companyOverview"
              :title="$t('CompanyProfile.companyOverviewHeader')"
              :contents="profile.companyOverview"
          />
          <InfoText
              class="mb-3"
              v-if="profile.profileOverview"
              :title="$t('CompanyProfile.profileHeader')"
              :contents="profile.profileOverview"
          />
        </div>
      </div>

      <!-- Sidebar Card -->
      <div class="card area-sidebar">
        <div class="card-body">
          <div class="mb-4" v-if="profile.headquarterCountry">
            <h5 class="proxima-blue-text">{{ $t("CompanyProfile.headquartersHeader") }}</h5>
            <div class="text-sm" @click="goToCountryProfile" style="cursor: pointer">
              {{ profile.headquarterCountry }}
            </div>
          </div>
          <div class="mb-4" v-if="profile.ownerType">
            <h5 class="proxima-blue-text">{{ $t("CompanyProfile.ownershipHeader") }}</h5>
            <div class="text-sm" style="text-transform: capitalize">
              {{ profile.ownerType?.toLowerCase() }}
            </div>
          </div>
          <div class="mb-4" v-if="stockExchanges.length">
            <h5 class="proxima-blue-text">{{ $t("CompanyProfile.investorOrExchangeHeader") }}</h5>
            <div class="text-sm" v-for="nth of stockExchanges" :key="nth.stockExchange">
              {{ nth.stockExchange ? `${nth.stockExchange}: ${(nth.stocks || []).join(", ")}` : "" }}
            </div>
          </div>
          <div class="mb-4" v-if="relatedAssets.length">
            <h5 class="proxima-blue-text">{{ $t("CompanyProfile.relatedAssetsHeader") }}</h5>
            <div
                class="text-sm"
                v-for="nth of relatedAssets"
                :key="nth.uuid"
                @click="goToAssetProfile(nth)"
                :style="isSubscribedToAssetProfile(nth) ? 'cursor: pointer' : ''"
            >
              {{ $t("CompanyProfile.relatedAsset", { asset: nth.shortName || nth.name, country: nth.countryName }) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Map Card -->
      <div class="card area-map">
        <div class="card-body px-0 pb-1">
          <iframe
              id="gmap_canvas"
              width="100%"
              height="400"
              :src="`https://maps.google.com/maps?q=${profile.coordinates}&t=k&z=5&ie=UTF8&iwloc=&output=embed`"
              frameborder="0"
              scrolling="no"
              marginheight="0"
              marginwidth="0"
          ></iframe>
          <div class="m-2 mx-3">
            {{ $t("CompanyProfile.locationFooter", { country: profile.headquarterCountry }) }}
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, computed, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { sortBy, uniqBy } from "lodash";
import { ROUTES } from "../../routes/names";
import { NSkeleton, NButton } from "naive-ui";
import {
  MOUNTED_COMPANY_PROFILE as profile,
  getCompanyProfile,
  inProgress,
  MOUNTED_COMPANY_PROFILE,
} from "../../composables/profiles/company";
import InfoText from "../../components/dumb/ProfileInfoText";
import { NOTES_WINDOW, SHOW_ALL_NOTES, has_notes } from "../../composables/notes/list";
import { getCountryProfile, MOUNTED_COUNTRY_PROFILE } from "../../composables/profiles/country";
import { PUBLISHED_PROFILES } from "../../composables/profiles/list";
import { getAssetProfile, MOUNTED_ASSET_PROFILE } from "../../composables/profiles/asset";
import { SUBSCRIPTION_INFO } from "../../composables/subscription";
import { logUserAction, USER_ACTIONS } from "@/composables/userLogs";

const route = useRoute();
const router = useRouter();

onMounted(async () => {
  const uuid = route.params?.id;
  if (!uuid) router.push({ name: ROUTES.PROXIMA_PROFILES.name });
  if (!profile.value) await getCompanyProfile(uuid);

  // Track company profile access
  const companyName = profile.value?.companyName || profile.value?.name || uuid;
  logUserAction(
    USER_ACTIONS.VIEW_COMPANY_PROFILE,
    `Viewed Company Profile: "${companyName}"`,
    { endpoint: `/company/${uuid}` }
  );
});

onBeforeUnmount(() => { profile.value = null; });

const addNote = () => {
  NOTES_WINDOW.value = true;
  SHOW_ALL_NOTES.value = false;
};

const stockExchanges = computed(() =>
    (profile.value?.stockExchanges || []).filter((nth) => nth.stockExchange !== null)
);

const relatedAssets = computed(() => {
  const assetProfiles = PUBLISHED_PROFILES.value?.assetProfiles || [];
  const tmp = [];
  for (const nth of assetProfiles) {
    const ownerIds = (nth?.owners || []).map((ith) => ith.id);
    if (ownerIds.includes(profile.value?.companyId)) tmp.push(nth);
  }
  return sortBy(tmp, "name");
});

const isSubscribedToAssetProfile = (nth) => {
  const element = route.query.element;
  const allElements = [
    ...new Set([
      ...(nth.byProductCommodities || []),
      ...(nth.nonCommercialCommodities || []),
      ...(nth.primaryCommodities || []),
    ]),
  ];
  return (
      allElements.includes(element) &&
      SUBSCRIPTION_INFO.elements?.map((nth) => nth.code).includes(element)
  );
};

const goToCountryProfile = async () => {
  const profileType = 'COUNTRY';
  const element = route.query.element;
  await getCountryProfile({ element, uuid: null, code: profile.value?.countryCode });
  nextTick(() => {
    if (MOUNTED_COUNTRY_PROFILE.value?.uuid) {
      const uuid = MOUNTED_COUNTRY_PROFILE.value?.uuid;
      router.push({
        name: ROUTES.PROXIMA_PROFILES.name,
        query: { _: Math.random(), profileType, uuid, element },
      });
    } else window.$message.info("No profile found");
  });
};

const goToCompanyProfile = async (id) => {
  const element = route.query.element;
  await getCompanyProfile({ element, uuid: null, id });
  nextTick(() => {
    if (MOUNTED_COMPANY_PROFILE.value?.uuid) {
      router.push({
        name: ROUTES.COMPANY_PROFILE.name,
        params: { id: MOUNTED_COMPANY_PROFILE.value?.uuid },
        query: { element },
      });
    } else window.$message.info("No profile found");
  });
};

const goToAssetProfile = async (nth) => {
  if (!isSubscribedToAssetProfile(nth)) {
    window.$message.info("No profile found");
    return;
  }
  const element = route.query.element;
  await getAssetProfile({ element, uuid: nth.uuid });
  nextTick(() => {
    if (MOUNTED_ASSET_PROFILE.value?.uuid) {
      router.push({
        name: ROUTES.ASSET_PROFILE.name,
        params: { id: MOUNTED_ASSET_PROFILE.value?.uuid },
        query: { element },
      });
    } else window.$message.info("No profile found");
  });
};
</script>

<style lang="scss" scoped>
* { border-radius: 0 !important; }

.profile-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr;
  grid-template-areas:
    "main"
    "sidebar"
    "map";

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "main sidebar"
      "map  map";
  }

  @media (min-width: 992px) {
    grid-template-columns: 2fr 1fr;
    grid-template-areas:
      "main sidebar"
      "map  map";
  }
}

.area-main    { grid-area: main; }
.area-sidebar { grid-area: sidebar; }
.area-map     { grid-area: map; }
</style>